/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/accounts/AccountProductsSection.tsx
 * PURPOSE: AccountDrawer product entitlements — assign catalog products, track adoption (account products)
 * PUBLIC_API: AccountProductsSection
 */

import { Package, Plus } from 'lucide-react';
import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import type { CrmAccountProduct } from '@shared/lib/frontend/api/crmAccountProductsApi';
import { useCrmAccountProducts } from '../../hooks/useCrmAccountProducts';
import { useCrmProductCatalog } from '../../hooks/useCrmProductCatalog';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { formatCrmCurrency } from '../../utils/formatCurrency';
import {
  PRODUCT_ADOPTION_STATUSES,
  productAdoptionLabel,
  productAdoptionPillClass,
} from '../../utils/productAdoptionLabel';
import { CollapsibleSection } from '../contacts/ContactDrawerBodyChrome';
import { EmptyState } from '../shared/EmptyState';

export type AccountProductsSectionProps = {
  accountId: string;
  readOnly?: boolean;
  /** Drawer uses collapsible section; page tab uses full-width layout with heading. */
  variant?: 'drawer' | 'page';
};

function EntitlementRow({
  row,
  locale,
  currency,
  readOnly,
  busy,
  onStatusChange,
  onRemove,
}: {
  row: CrmAccountProduct;
  locale: string;
  currency: string;
  readOnly: boolean;
  busy: boolean;
  onStatusChange: (status: CrmAccountProduct['status']) => void;
  onRemove: () => void;
}) {
  const mrrLabel =
    row.mrr != null && Number.isFinite(row.mrr)
      ? formatCrmCurrency(row.mrr, currency, locale)
      : null;

  return (
    <li
      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-900/30"
      data-testid={`account-product-row-${row.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{row.productName}</p>
          {row.productCategory ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{row.productCategory}</p>
          ) : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {readOnly ? (
              <span
                className={twMerge(
                  'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                  productAdoptionPillClass(row.status)
                )}
              >
                {productAdoptionLabel(row.status)}
              </span>
            ) : (
              <select
                className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold dark:border-gray-600 dark:bg-gray-800"
                value={row.status}
                disabled={busy}
                data-testid={`account-product-status-${row.id}`}
                onChange={(e) => onStatusChange(e.target.value as CrmAccountProduct['status'])}
                aria-label={`Status for ${row.productName}`}
              >
                {PRODUCT_ADOPTION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {productAdoptionLabel(s)}
                  </option>
                ))}
              </select>
            )}
            {row.seats != null ? (
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                {row.seats} {row.seats === 1 ? 'seat' : 'seats'}
              </span>
            ) : null}
          </div>
        </div>
        {mrrLabel ? (
          <span className="shrink-0 text-sm font-medium tabular-nums text-gray-800 dark:text-gray-200">
            {mrrLabel}/mo
          </span>
        ) : null}
      </div>
      {!readOnly ? (
        <button
          type="button"
          className="mt-2 text-[10px] font-medium text-rose-600 hover:underline dark:text-rose-400"
          disabled={busy}
          data-testid={`account-product-remove-${row.id}`}
          onClick={onRemove}
        >
          Remove
        </button>
      ) : null}
    </li>
  );
}

export function AccountProductsSection({
  accountId,
  readOnly = false,
  variant = 'drawer',
}: AccountProductsSectionProps): ReactElement {
  const { currency, locale } = useTenantCurrency();
  const {
    entitlements,
    loading,
    creating,
    patching,
    deleting,
    createEntitlement,
    patchEntitlement,
    deleteEntitlement,
  } = useCrmAccountProducts(accountId);
  const { products: catalog, loading: catalogLoading } = useCrmProductCatalog(!readOnly);

  const [showForm, setShowForm] = useState(false);
  const [productId, setProductId] = useState('');
  const [status, setStatus] = useState<CrmAccountProduct['status']>('evaluation');
  const [formError, setFormError] = useState<string | null>(null);

  const assignedIds = useMemo(() => new Set(entitlements.map((e) => e.productId)), [entitlements]);
  const availableProducts = useMemo(
    () => catalog.filter((p) => !assignedIds.has(p.id)),
    [catalog, assignedIds]
  );

  const busy = creating || patching || deleting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!productId) {
      setFormError('Select a product from your catalog.');
      return;
    }
    try {
      await createEntitlement({ productId, status });
      setShowForm(false);
      setProductId('');
      setStatus('evaluation');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not assign product.');
    }
  }

  const productsBody = (
    <div className="space-y-3" data-testid="account-section-products">
        {!readOnly && !showForm ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            data-testid="account-product-add"
            onClick={() => setShowForm(true)}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Assign product
          </button>
        ) : null}

        {showForm ? (
          <form className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-900/40" onSubmit={(e) => void handleSubmit(e)}>
            {formError ? <p className="text-xs text-rose-600 dark:text-rose-400">{formError}</p> : null}
            {catalogLoading ? (
              <p className="text-xs text-gray-500">Loading catalog…</p>
            ) : availableProducts.length === 0 ? (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                No products available.{' '}
                <Link to="/sales-profile?tab=products" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                  Manage catalog
                </Link>
              </p>
            ) : (
              <select
                className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                data-testid="account-product-select"
                aria-label="Product from catalog"
              >
                <option value="">Select product…</option>
                {availableProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.product_name}
                    {p.category ? ` · ${p.category}` : ''}
                  </option>
                ))}
              </select>
            )}
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
              value={status}
              onChange={(e) => setStatus(e.target.value as CrmAccountProduct['status'])}
              aria-label="Adoption status"
            >
              {PRODUCT_ADOPTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {productAdoptionLabel(s)}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy || !productId}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                data-testid="account-product-save"
              >
                {busy ? 'Saving…' : 'Assign'}
              </button>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300"
                onClick={() => {
                  setShowForm(false);
                  setFormError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {loading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading products…</p>
        ) : entitlements.length === 0 ? (
          <EmptyState
            title="No products assigned"
            description="Track which offerings this account is evaluating, piloting, or actively using."
            icon={<Package className="h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden />}
          />
        ) : (
          <ul className="space-y-2">
            {entitlements.map((row) => (
              <EntitlementRow
                key={row.id}
                row={row}
                locale={locale}
                currency={currency}
                readOnly={readOnly}
                busy={busy}
                onStatusChange={(s) => void patchEntitlement({ id: row.id, patch: { status: s } })}
                onRemove={() => void deleteEntitlement(row.id)}
              />
            ))}
          </ul>
        )}
      </div>
  );

  if (variant === 'page') {
    return (
      <div className="space-y-4" data-testid="account-products-page">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Product opportunities</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Track which offerings this account is evaluating, piloting, or actively using.
          </p>
        </div>
        {productsBody}
      </div>
    );
  }

  return <CollapsibleSection title="Products & entitlements">{productsBody}</CollapsibleSection>;
}
